import { containers, elements, feedback } from '@ecosystem/newron-design-system';
import { capitalize, isEmpty } from 'lodash';
import React from 'react';
import { renderVisualization } from '@data-visualization/utilities/ChartUtils';
import { ComponentName, initialTrendsKpi, TrendsKpi } from '@models/Products';
import { RI_PRODUCT_TREND_TOP_5_PRODUCTS_CARD_TITLE } from '@pages/Utilities';
import { useInViewTracking } from '@utils/hook/tracking/UseInViewTracking';
import { ElementType } from '@utils/page-analytics/model/AnalyticTrackingBasePropertiesObj';
import { useGeneratePageElementViewParam } from '@utils/page-analytics/model/PageElementViewedTracking';
import { FactRevenue, Filters, PageName, PageType, SubSection } from '@utils/pageConfig/Types';
import { GrowthTypes } from '../comparison-growth-selector/Types';
import { DisplayOptions } from '../top-selling-products/products-table-filter/Types';
import ProductsTable from '../top-selling-products/ProductsTable';
import { getProductsColumns } from './products-top-5-table-columns/ProductsTop5TableColumns';
import ProductsTop5TableFilter from './products-top-5-table-filter/ProductsTop5TableFilter';
import { NoResult } from '@pages/NoResult';
import { usePageConfig } from '@utils/pageConfig/PagesConfigContext';
import ErrorResultMessage from '@pages/ErrorResultMessage';

interface Props {
  trendsKpi: TrendsKpi;
  groupedTop5FilterState: any;
  factRevenue: FactRevenue;
  isLoading: boolean;
  prodLeaderboardFeatureProductFilters: Filters[];
  isApiError?: boolean;
}

const renderTop5Table = (
  trendsKpi: TrendsKpi,
  groupedTop5FilterState: any,
  factRevenue: FactRevenue,
  isLoading: boolean,
  pageElementViewTrackRef: (node?: Element | null) => void,
  prodLeaderboardFeatureProductFilters: Filters[],
  isApiError: boolean,
) => {
  const content = renderVisualization({
    isLoading: isLoading,
    fetchedApiData: trendsKpi,
    defaultData: initialTrendsKpi,
    jsxToRender: (
      <ProductsTable
        productsLeaderboard={trendsKpi.items}
        selectedDisplayType={DisplayOptions.trends}
        selectedSaleType={groupedTop5FilterState.selectedSaleType}
        factRevenue={factRevenue}
        selectedGrowthType={GrowthTypes.actual}
        setSelectedSaleType={groupedTop5FilterState.setSelectedSaleType}
        isViewTypeChanged={false}
        tableColumns={getProductsColumns(groupedTop5FilterState.selectedFocusArea)}
        enableSort={false}
        selectedFocusArea={groupedTop5FilterState.selectedFocusArea}
        isTableHeaderSticky={false}
        prodLeaderboardFeatureProductFilters={prodLeaderboardFeatureProductFilters}
      />
    ),
    noResultJsx: <div ref={pageElementViewTrackRef}>{NoResult()}</div>,
    errorMessageJsx: <div ref={pageElementViewTrackRef}>{ErrorResultMessage()}</div>,
    isError: isApiError,
  });

  return (
    <div ref={pageElementViewTrackRef}>
      <ProductsTop5TableFilter
        selectedKpi={groupedTop5FilterState.selectedKpi}
        selectedFocusArea={groupedTop5FilterState.selectedFocusArea}
        selectedSaleType={groupedTop5FilterState.selectedSaleType}
        setSelectedKpi={groupedTop5FilterState.setSelectedKpi}
        setSelectedFocusArea={groupedTop5FilterState.setSelectedFocusArea}
        setSelectedSaleType={groupedTop5FilterState.setSelectedSaleType}
        isLoading={isLoading}
      />
      {content}
    </div>
  );
};

const ProductsTop5TableCard: React.FC<Props> = ({
  trendsKpi,
  groupedTop5FilterState,
  factRevenue,
  isLoading,
  prodLeaderboardFeatureProductFilters,
  isApiError = false,
}) => {
  const pageElementViewParam = useGeneratePageElementViewParam();
  const pagesConfig = usePageConfig();
  const customPageElementViewParam = () => ({
    ...pageElementViewParam,
    pageType: PageType.RI_PRODUCT,
    pageName: PageName.RI_PRODUCT,
    subSection: SubSection.PRODUCT_TRENDS,
    componentName: ComponentName.PRODUCT_TRENDS_TOP_5_PRODUCTS,
    elementType: ElementType.TABLE,
    elementKpi: capitalize(groupedTop5FilterState.selectedSaleType),
    hasData: !isEmpty(trendsKpi?.items),
    access: pagesConfig.access,
  });

  const { ref } = useInViewTracking(customPageElementViewParam);

  const { Card } = containers;
  const { Tooltip } = feedback;
  return (
    <Card>
      <Card.Title title={RI_PRODUCT_TREND_TOP_5_PRODUCTS_CARD_TITLE}>
        <div className="flex pl-4">
          <Tooltip testId="products-top-5-tooltip" placement="top" theme="light">
            <Tooltip.Content>
              <p>
                Discover which items are having a big impact - select from a variety of discovery contexts, choose to
                explore Market drivers or your own range, and base your search on Revenue or Unit results.
              </p>
            </Tooltip.Content>
            <div className="mt-1.5">
              <elements.Icon color="brand">
                <elements.Icon.icons.HelpOutline />
              </elements.Icon>
            </div>
          </Tooltip>
        </div>
      </Card.Title>
      {renderTop5Table(
        trendsKpi,
        groupedTop5FilterState,
        factRevenue,
        isLoading,
        ref,
        prodLeaderboardFeatureProductFilters,
        isApiError,
      )}
    </Card>
  );
};

export default ProductsTop5TableCard;
